import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  AlertTriangle,
  AlertCircle,
  FileQuestion,
  Clock,
  CheckCircle,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
  FileX,
  FileWarning,
  HelpCircle,
  Calendar,
  Users,
  Tag,
  Lightbulb,
  BarChart3,
  ArrowUpRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const priorityConfig = {
  critical: { color: "bg-red-100 text-red-700 border-red-200", icon: AlertTriangle, label: "Critical" },
  high: { color: "bg-orange-100 text-orange-700 border-orange-200", icon: AlertCircle, label: "High" },
  medium: { color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Clock, label: "Medium" },
  low: { color: "bg-blue-100 text-blue-700 border-blue-200", icon: HelpCircle, label: "Low" }
};

const gapTypeConfig = {
  missing: { color: "bg-red-50 text-red-600", icon: FileX, label: "Missing Article" },
  incomplete: { color: "bg-amber-50 text-amber-600", icon: FileWarning, label: "Incomplete" },
  unclear: { color: "bg-purple-50 text-purple-600", icon: HelpCircle, label: "Unclear" },
  outdated: { color: "bg-slate-50 text-slate-600", icon: Clock, label: "Outdated" }
};

const statusConfig = {
  open: { color: "bg-red-100 text-red-700", label: "Open" },
  in_progress: { color: "bg-yellow-100 text-yellow-700", label: "In Progress" },
  resolved: { color: "bg-emerald-100 text-emerald-700", label: "Resolved" }
};

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card className="bg-white border-slate-200 shadow-sm">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const GapCard = ({ gap, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  const priority = priorityConfig[gap.priority] || priorityConfig.medium;
  const gapType = gapTypeConfig[gap.gap_type] || gapTypeConfig.incomplete;
  const status = statusConfig[gap.status] || statusConfig.open;
  const PriorityIcon = priority.icon;
  const GapTypeIcon = gapType.icon;

  return (
    <motion.div
      layout
      className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden"
    >
      <div
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${priority.color} flex items-center justify-center flex-shrink-0`}>
            <PriorityIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-medium text-slate-900">{gap.title}</span>
              <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge variant="outline" className={`${gapType.color} text-xs border-0`}>
                <GapTypeIcon className="w-3 h-3 mr-1" />
                {gapType.label}
              </Badge>
              <Badge variant="outline" className="text-xs border-slate-200">
                <Tag className="w-3 h-3 mr-1" />
                {gap.category}
              </Badge>
              <Badge variant="outline" className="text-xs border-slate-200">
                <Users className="w-3 h-3 mr-1" />
                {gap.user_type}
              </Badge>
              {gap.ticket_count > 0 && (
                <Badge variant="outline" className="text-xs border-slate-200">
                  <BarChart3 className="w-3 h-3 mr-1" />
                  {gap.ticket_count} tickets
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-600 line-clamp-2">{gap.description}</p>
          </div>
          <div className="flex-shrink-0">
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-4">
              {/* Sample Queries */}
              {gap.sample_queries && gap.sample_queries.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    Sample Customer Queries
                  </h4>
                  <div className="space-y-2">
                    {gap.sample_queries.map((query, idx) => (
                      <div key={`${gap.id}-query-${idx}`} className="p-2 bg-slate-50 rounded-lg text-sm text-slate-600 italic">
                        &quot;{query}&quot;
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested Action */}
              {gap.suggested_action && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    Suggested Action
                  </h4>
                  <p className="text-sm text-slate-600 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                    {gap.suggested_action}
                  </p>
                </div>
              )}

              {/* Status Update */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span className="text-sm text-slate-500">Update Status:</span>
                <div className="flex gap-2">
                  {["open", "in_progress", "resolved"].map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={gap.status === s ? "default" : "outline"}
                      onClick={(e) => {
                        e.stopPropagation();
                        onStatusChange(gap.id, s);
                      }}
                      className={gap.status === s ? "bg-[#425B76]" : ""}
                    >
                      {statusConfig[s].label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const KnowledgeBasePage = () => {
  const [gaps, setGaps] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState(null);
  const [ticketsAnalyzed, setTicketsAnalyzed] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categories, setCategories] = useState([]);

  const fetchGaps = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (priorityFilter !== "all") params.append("priority", priorityFilter);
      if (typeFilter !== "all") params.append("gap_type", typeFilter);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await axios.get(`${API}/kb-gaps?${params.toString()}`, {
        withCredentials: true
      });
      
      setGaps(response.data.gaps || []);
      setSummary(response.data.summary || {});
      setLastAnalysis(response.data.last_analysis);
      setTicketsAnalyzed(response.data.tickets_analyzed || 0);
    } catch (error) {
      // Error handled silently
      toast.error("Failed to fetch knowledge base gaps");
    } finally {
      setLoading(false);
    }
  }, [priorityFilter, typeFilter, categoryFilter, statusFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kb-gaps/categories`, {
        withCredentials: true
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error("Failed to load gap categories");
    }
  }, []);

  useEffect(() => {
    fetchGaps();
    fetchCategories();
  }, [fetchGaps, fetchCategories]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/kb-gaps/analyze`, {}, {
        withCredentials: true
      });
      toast.success(`Analysis complete! Found ${response.data.gaps_found} knowledge gaps`);
      fetchGaps();
      fetchCategories();
    } catch (error) {
      // Error handled silently
      toast.error(error.response?.data?.detail || "Failed to run analysis");
    } finally {
      setAnalyzing(false);
    }
  };

  const updateGapStatus = async (gapId, newStatus) => {
    try {
      await axios.put(`${API}/kb-gaps/${gapId}`, { status: newStatus }, {
        withCredentials: true
      });
      setGaps(gaps.map(g => g.id === gapId ? { ...g, status: newStatus } : g));
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Filter gaps by search query
  const filteredGaps = gaps.filter(gap => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      gap.title?.toLowerCase().includes(query) ||
      gap.description?.toLowerCase().includes(query) ||
      gap.category?.toLowerCase().includes(query) ||
      gap.sample_queries?.some(q => q.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <Layout title="Knowledge Base Gaps">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#425B76]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Knowledge Base Gaps">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-slate-500">
              AI-powered analysis of support tickets to identify knowledge base improvements
            </p>
            {lastAnalysis && (
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Last analyzed: {new Date(lastAnalysis).toLocaleString()} ({ticketsAnalyzed} tickets)
              </p>
            )}
          </div>
          <Button
            onClick={runAnalysis}
            disabled={analyzing}
            className="bg-[#425B76] hover:bg-[#2D4156] gap-2"
            data-testid="run-analysis-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Re-run Analysis
              </>
            )}
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Gaps"
            value={summary.total || 0}
            icon={BookOpen}
            color="bg-[#425B76]/10 text-[#425B76]"
          />
          <StatCard
            title="Critical Priority"
            value={summary.critical || 0}
            icon={AlertTriangle}
            color="bg-red-100 text-red-600"
          />
          <StatCard
            title="Missing Articles"
            value={summary.missing || 0}
            icon={FileX}
            color="bg-orange-100 text-orange-600"
          />
          <StatCard
            title="Open Issues"
            value={summary.open || 0}
            icon={AlertCircle}
            color="bg-amber-100 text-amber-600"
            subtitle={`${summary.resolved || 0} resolved`}
          />
        </div>

        {/* Filters */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search gaps..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              
              {/* Priority Filter */}
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[150px]" data-testid="priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              {/* Type Filter */}
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]" data-testid="type-filter">
                  <SelectValue placeholder="Gap Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="missing">Missing</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                  <SelectItem value="unclear">Unclear</SelectItem>
                  <SelectItem value="outdated">Outdated</SelectItem>
                </SelectContent>
              </Select>

              {/* Category Filter */}
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[150px]" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Gaps List */}
        {filteredGaps.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">
                {gaps.length === 0 ? "No Knowledge Gaps Found" : "No Results Match Filters"}
              </h3>
              <p className="text-slate-500 mb-4">
                {gaps.length === 0 
                  ? "Run an analysis to identify knowledge base gaps from your support tickets."
                  : "Try adjusting your search or filters."}
              </p>
              {gaps.length === 0 && (
                <Button
                  onClick={runAnalysis}
                  disabled={analyzing}
                  className="bg-[#425B76] hover:bg-[#2D4156] gap-2"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Run Analysis
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Showing {filteredGaps.length} of {gaps.length} gaps
            </p>
            {filteredGaps.map((gap) => (
              <GapCard
                key={gap.id}
                gap={gap}
                onStatusChange={updateGapStatus}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default KnowledgeBasePage;
